import { PaymentElement } from '@stripe/react-stripe-js';
import React, { useState } from 'react';
import { Controller, FormProvider } from 'react-hook-form';
import { Col } from 'react-styled-flexboxgrid';

import {
  BUTTON_TYPES,
  CURRENCIES,
  INPUT_TYPES,
} from '@savgroup-front-common/constants/shared';
import { Button } from '@savgroup-front-common/core/atoms/button';
import { Input } from '@savgroup-front-common/core/atoms/Form';
import { SafeFormattedMessageWithoutSpread } from '@savgroup-front-common/core/formatters';
import { formatStripeAmount } from '@savgroup-front-common/core/formatters/intl';
import { ClosedLockUnfilled } from '@savgroup-front-common/core/protons/icons';

import useCheckoutForm from './CheckoutForm.hooks';
import {
  $PaymentButtonContainer,
  $PaymentContainer,
  $PaymentInputContainer,
} from './CheckoutForm.styles';
import messages from './messages';

interface CheckoutFormProps {
  onSubmitSuccess: () => Promise<void>;
  isFormCompleted: boolean;
  invoice: { id: string; priceWithTax: number; currencyTrigram: CURRENCIES };
}

const CheckoutForm: React.FC<CheckoutFormProps> = ({
  onSubmitSuccess,
  isFormCompleted,
  invoice,
}) => {
  const [paymentIsComplete, setPaymentIsComplete] = useState(false);
  const { formContext, onSubmit, paymentLoading } = useCheckoutForm({
    onSubmitSuccess,
    invoiceId: invoice.id,
  });

  const {
    control,
    formState: { errors, isValid },
  } = formContext;

  return (
    <$PaymentContainer data-testid="PaymentContainer">
      <FormProvider {...formContext}>
        <form onSubmit={onSubmit}>
          <$PaymentInputContainer data-testid="PaymentInputContainer">
            <Col sm={12} xs={12}>
              <Controller
                control={control}
                name="name"
                render={({ field }) => {
                  return (
                    <Input
                      label={messages.creditCardHolder}
                      type={INPUT_TYPES.TEXT}
                      value={field.value}
                      errors={errors}
                      onChange={field.onChange}
                      isRequired
                      dataTestId="creditCardHolder"
                    />
                  );
                }}
              />
            </Col>
          </$PaymentInputContainer>
          <div data-testid="StripeElement">
            <PaymentElement
              onChange={(event) => {
                if (event.complete) {
                  setPaymentIsComplete(true);
                }
              }}
            />
          </div>
          <$PaymentButtonContainer>
            <Button
              fluid
              primary
              isLoading={paymentLoading}
              disabled={!isValid || !paymentIsComplete || !isFormCompleted}
              type={BUTTON_TYPES.SUBMIT}
              isError={Object.keys(errors).length > 0}
              icon={<ClosedLockUnfilled />}
              dataTestId="paymentFormSubmitButton"
            >
              <SafeFormattedMessageWithoutSpread
                message={messages.pay}
                values={{
                  amount: formatStripeAmount(
                    invoice.priceWithTax,
                    invoice.currencyTrigram ?? CURRENCIES.EUR,
                  ),
                }}
              />
            </Button>
          </$PaymentButtonContainer>
        </form>
      </FormProvider>
    </$PaymentContainer>
  );
};

export default CheckoutForm;
